'use client'

import { useEffect, useSyncExternalStore } from 'react'
import { logError } from '@/shared/lib/logError'

/** Язык падения — по первому сегменту адреса; адрес за жизнь экрана не меняется. */
const NEVER_CHANGES = () => () => {}
type ErrorLocale = 'ru' | 'en'
const pathLocale = (): ErrorLocale => (/^\/en(\/|$)/.test(window.location.pathname) ? 'en' : 'ru')
const serverLocale = (): ErrorLocale => 'ru'

/**
 * Тексты экрана. Словарей здесь нет (next-intl живёт в упавшем layout'е), поэтому обе
 * редакции — прямо в коде; сторож check-i18n знает этот файл как исключение.
 */
const TEXT = {
    ru: {
        title: 'Что-то сломалось у нас',
        lines: ['Твои записи целы. Перезагрузи страницу через минуту.', 'Something broke on our side. Please reload in a minute.'],
        reload: 'Обновить · Reload',
    },
    en: {
        title: 'Something broke on our side',
        lines: ['Your notes are safe. Please reload the page in a minute.'],
        reload: 'Reload',
    },
} as const

/**
 * ПОСЛЕДНЯЯ ГРАНИЦА: падение самого корневого layout'а.
 *
 * Она подменяет собой ВЕСЬ документ, поэтому обязана отдать `<html>` и `<body>`
 * сама — и поэтому же здесь нет ничего из обычной страницы: ни globals.css, ни
 * шрифтов next/font, ни next-intl, ни AuthProvider. Классы `.err-sheet` и её
 * разлиновка сюда не доезжают, так что лист собран инлайновыми стилями — теми же
 * значениями холста (разлиновка 27/28px, поле слева терракотой, моно-кикер), но
 * без переменных: `tokens.css` тоже не загружен.
 *
 * ТЕКСТ ЗАШИТ. Локали в этот момент не существует, поэтому язык берём из адреса: на /en/*
 * лист целиком английский. В остальных случаях — как было: русская строка и английская под
 * ней, потому что человек может быть любым, а угадывание дороже второй строки. Сервер
 * адреса не видит и рисует двуязычный вариант; на /en клиент сразу после гидрации меняет
 * его на английский (useSyncExternalStore — без рассинхрона гидрации).
 * Кнопка одна: перезагрузить. Ссылка «в библиотеку» отсюда врала бы — если упал
 * корневой layout, кабинет тоже не откроется.
 */
export default function GlobalError({ error, reset }: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    useEffect(() => {
        logError('global-error-boundary', error, { digest: error.digest })
    }, [error])
    const locale = useSyncExternalStore(NEVER_CHANGES, pathLocale, serverLocale)
    const text = TEXT[locale]

    return (
        <html lang={locale}>
            <body style={{ margin: 0, background: '#F4F2EA', color: '#1F1E1D', fontFamily: 'Georgia, "Times New Roman", serif' }}>
                <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', boxSizing: 'border-box' }}>
                    <div
                        style={{
                            position: 'relative',
                            width: '30rem',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            padding: '2rem 2rem 2rem calc(2rem + 14px)',
                            border: '1px solid #D4CFC0',
                            borderRadius: '2px',
                            background: '#FAF9F5 repeating-linear-gradient(to bottom, transparent 0 27px, #E5E1D6 27px 28px)',
                            backgroundPosition: '0 12px',
                        }}
                    >
                        <div style={{ position: 'absolute', left: 22, top: 0, bottom: 0, width: 1, background: '#8B6F47', opacity: 0.55 }} />
                        <p style={{ margin: 0, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: '0.6875rem', letterSpacing: '.08em', textTransform: 'uppercase', color: '#7A6039', lineHeight: '28px' }}>
                            {error.digest ? `500 · id ${error.digest}` : '500'}
                        </p>
                        <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 500, lineHeight: '28px', letterSpacing: '-.01em' }}>
                            {text.title}
                        </h1>
                        {text.lines.map(line => (
                            <p key={line} style={{ margin: 0, fontStyle: 'italic', fontSize: '1rem', lineHeight: '28px', color: '#6B6B63' }}>
                                {line}
                            </p>
                        ))}
                        <div style={{ marginTop: 28 }}>
                            <button
                                type="button"
                                onClick={reset}
                                style={{ height: 44, padding: '0 1.5rem', borderRadius: '0.625rem', border: '1px solid #B4522F', background: '#B4522F', color: '#FAF9F5', fontFamily: 'system-ui, sans-serif', fontSize: '0.9375rem', fontWeight: 500, cursor: 'pointer' }}
                            >
                                {text.reload}
                            </button>
                        </div>
                    </div>
                </div>
            </body>
        </html>
    )
}
